"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Line, Doughnut } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from "chart.js"
import { useAuth } from "../../../contexts/AuthContext"
import {
  ChartBarIcon,
  PaperAirplaneIcon,
  EnvelopeIcon,
  WalletIcon,
  ChartPieIcon,
  CheckCircleIcon,
  EyeIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarIcon,
  ClockIcon,
  ArrowPathIcon,
  UsersIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline"
import Swal from "sweetalert2"

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
)

// Format INR currency
const formatCurrency = (value) => {
  const num = typeof value === "number" ? value : Number(value) || 0
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

function AdminDashboard() {
  const navigate = useNavigate()
  const { user, getAllUsers, fetchReportStats, getUserBalance } = useAuth()

  const [loading, setLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(new Date())

  // Data states
  const [adminBalance, setAdminBalance] = useState(user?.balance || 0)
  const [managedUsers, setManagedUsers] = useState([])
  const [userStatsMap, setUserStatsMap] = useState({})
  const [safeToday, setSafeToday] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    pending: 0,
  })
  const [yesterdayData, setYesterdayData] = useState({ total: 0, delivered: 0, read: 0 })
  const [weeklyData, setWeeklyData] = useState({
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    messages: [0, 0, 0, 0, 0, 0, 0],
    delivered: [0, 0, 0, 0, 0, 0, 0],
    read: [0, 0, 0, 0, 0, 0, 0],
    failed: [0, 0, 0, 0, 0, 0, 0],
  })

  // Table Search & Filter
  const [searchTerm, setSearchTerm] = useState("")
  const [roleFilter, setRoleFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")

  useEffect(() => {
    loadDashboardData()
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    const refreshTimer = setInterval(loadDashboardData, 60000)
    return () => {
      clearInterval(timer)
      clearInterval(refreshTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadDashboardData = async () => {
    const todayStr = new Date().toISOString().split("T")[0]
    const yesterdayDate = new Date(Date.now() - 24 * 60 * 60 * 1000)
    const yesterdayStr = yesterdayDate.toISOString().split("T")[0]

    try {
      // Fast initial load: balance + users + today/yesterday overview in 1 parallel batch
      const [balRes, usersRes, todayStatsRes, yesterdayStatsRes] = await Promise.all([
        getUserBalance ? getUserBalance().catch(() => null) : Promise.resolve(null),
        getAllUsers ? getAllUsers().catch(() => null) : Promise.resolve(null),
        fetchReportStats({ startDate: todayStr, endDate: todayStr }).catch(() => null),
        fetchReportStats({ startDate: yesterdayStr, endDate: yesterdayStr }).catch(() => null),
      ])

      if (balRes?.success && typeof balRes.balance === "number") {
        setAdminBalance(balRes.balance)
      }

      let usersList = []
      if (usersRes?.success && Array.isArray(usersRes.users)) {
        const currentUserId = String(user?._id || user?.id || "")
        usersList = usersRes.users.filter((u) => String(u._id) !== currentUserId)
        setManagedUsers(usersList)
      }

      if (todayStatsRes) {
        setSafeToday(todayStatsRes)
      }
      if (yesterdayStatsRes) {
        setYesterdayData(yesterdayStatsRes)
      }

      // Unblock UI immediately! Page renders in <200ms
      setLoading(false)

      // Async non-blocking background loading
      loadWeeklyDataAsync()
      loadUserStatsAsync(usersList, todayStr)

    } catch (error) {
      console.error("Error loading Admin Dashboard:", error)
      setLoading(false)
    }
  }

  const loadWeeklyDataAsync = async () => {
    try {
      const today = new Date()
      const dates = []
      const promises = []

      for (let i = 6; i >= 0; i--) {
        const d = new Date(today)
        d.setDate(d.getDate() - i)
        const dStr = d.toISOString().split("T")[0]
        dates.push({ label: d.toLocaleDateString("en-US", { weekday: "short" }), dStr })
        promises.push(fetchReportStats({ startDate: dStr, endDate: dStr }).catch(() => null))
      }

      const results = await Promise.all(promises)

      const weeklySent = []
      const weeklyDelivered = []
      const weeklyRead = []
      const weeklyFailed = []

      results.forEach((res) => {
        if (res) {
          weeklySent.push(res.sent || res.totalSent || 0)
          weeklyDelivered.push(res.delivered || 0)
          weeklyRead.push(res.read || 0)
          weeklyFailed.push(res.failed || 0)
        } else {
          weeklySent.push(0)
          weeklyDelivered.push(0)
          weeklyRead.push(0)
          weeklyFailed.push(0)
        }
      })

      setWeeklyData({
        labels: dates.map((d) => d.label),
        messages: weeklySent,
        delivered: weeklyDelivered,
        read: weeklyRead,
        failed: weeklyFailed,
      })
    } catch (err) {
      console.error("Error loading weekly trend async:", err)
    }
  }

  const loadUserStatsAsync = async (usersList, todayStr) => {
    if (!usersList || usersList.length === 0) return
    const statsMap = {}
    const batchSize = 10

    for (let i = 0; i < usersList.length; i += batchSize) {
      const batch = usersList.slice(i, i + batchSize)
      await Promise.all(
        batch.map(async (u) => {
          try {
            const res = await fetchReportStats({
              userId: u._id,
              startDate: todayStr,
              endDate: todayStr,
            })
            if (res) {
              statsMap[u._id] = {
                total: res.total || 0,
                sent: res.sent || res.totalSent || 0,
                delivered: res.delivered || 0,
                read: res.read || 0,
                failed: res.failed || 0,
              }
            }
          } catch (err) {
            // ignore error
          }
        })
      )
      setUserStatsMap((prev) => ({ ...prev, ...statsMap }))
    }
  }

  // Filter Users Table
  const filteredUsers = managedUsers.filter((u) => {
    const matchesSearch =
      (u.username || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.number || "").includes(searchTerm)
    const matchesRole = roleFilter === "all" ? true : u.role === roleFilter
    const matchesStatus = statusFilter === "all" ? true : (u.userStatus || "active") === statusFilter
    return matchesSearch && matchesRole && matchesStatus
  })

  // Calculations
  const attemptedToday = (safeToday.sent || 0) + (safeToday.delivered || 0) + (safeToday.read || 0) + (safeToday.failed || 0)
  const deliveredOrReadToday = (safeToday.delivered || 0) + (safeToday.read || 0)
  const successRateToday = attemptedToday > 0 ? Math.min(100, Math.round((deliveredOrReadToday / attemptedToday) * 100)) : 0

  const calculatePercentageChange = (todayVal, yesterdayVal) => {
    if (yesterdayVal === 0) return todayVal > 0 ? 100 : 0
    return Math.round(((todayVal - yesterdayVal) / yesterdayVal) * 100)
  }

  const todayPercentageChange = calculatePercentageChange(safeToday.total || 0, yesterdayData.total || 0)

  const getBoundedPercentage = (value, base) => {
    if (!base) return 0
    return Math.min(100, Math.round((Number(value || 0) / Number(base || 0)) * 100))
  }

  // Chart Configurations
  const messageChartData = {
    labels: weeklyData.labels,
    datasets: [
      {
        label: "Messages Sent",
        data: weeklyData.messages,
        borderColor: "#2563eb",
        backgroundColor: "rgba(37, 99, 235, 0.12)",
        pointBackgroundColor: "#2563eb",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Delivered",
        data: weeklyData.delivered,
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.12)",
        pointBackgroundColor: "#10b981",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Read",
        data: weeklyData.read,
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245, 158, 11, 0.12)",
        pointBackgroundColor: "#f59e0b",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Failed",
        data: weeklyData.failed,
        borderColor: "#ef4444",
        backgroundColor: "rgba(239, 68, 68, 0.12)",
        pointBackgroundColor: "#ef4444",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
    ],
  }

  const statusValues = [safeToday.sent, safeToday.delivered, safeToday.read, safeToday.failed, safeToday.pending]
  const hasStatusData = statusValues.some((v) => v > 0)

  const statusChartData = {
    labels: hasStatusData ? ["Sent", "Delivered", "Read", "Failed", "Pending"] : ["No data"],
    datasets: [
      {
        data: hasStatusData ? statusValues : [1],
        backgroundColor: hasStatusData ? ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#94a3b8"] : ["#e5e7eb"],
        borderWidth: 0,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  }

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: { legend: { position: "top" }, tooltip: { enabled: hasStatusData } },
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading Admin Dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800">
      <div className="mx-auto px-4 sm:px-4 lg:px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-yellow-600 bg-clip-text text-transparent mb-2 flex items-center">
                <ShieldCheckIcon className="w-8 h-8 inline-block mr-3 text-green-600" />
                Admin Dashboard - {user?.username || "Admin"}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">
                System administration and user campaign analytics overview
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  Admin Live Dashboard
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {currentTime.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {currentTime.toLocaleTimeString("en-US", { hour12: true })}
                </span>
              </div>
            </div>

            <div className="mt-4 lg:mt-0 flex gap-3">
              <Link to="/user-accounts">
                <button className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-yellow-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-yellow-700 transition-all duration-200 shadow-lg hover:shadow-xl">
                  <UsersIcon className="w-4 h-4 mr-2" />
                  User Accounts
                </button>
              </Link>
              <Link to="/admin-recharge">
                <button className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl">
                  <CreditCardIcon className="w-4 h-4 mr-2" />
                  Recharge User
                </button>
              </Link>
              <button
                onClick={loadDashboardData}
                className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <ArrowPathIcon className="w-5 h-5 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Top 4 Executive Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Managed Users */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {managedUsers.length}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Managed Users</div>
                <div className="flex items-center mt-2 text-green-600 dark:text-green-400 text-sm">
                  <UsersIcon className="w-4 h-4 mr-1" />
                  Active System Users
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <UsersIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Today System Messages */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {(safeToday.total || 0).toLocaleString()}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Today System Messages</div>
                <div
                  className={`flex items-center mt-2 text-sm ${
                    todayPercentageChange >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {todayPercentageChange >= 0 ? (
                    <ArrowUpIcon className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDownIcon className="w-4 h-4 mr-1" />
                  )}
                  {Math.abs(todayPercentageChange)}% from yesterday
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <PaperAirplaneIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Admin Wallet Balance */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {formatCurrency(adminBalance)}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Admin Wallet Balance</div>
                <div className="flex items-center mt-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  {formatCurrency(adminBalance)} Credits
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                <WalletIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* System Success Rate */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {successRateToday}%
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">System Success Rate</div>
                <div className="flex items-center mt-2 text-green-600 dark:text-green-400 text-sm">
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  {deliveredOrReadToday} delivered/read today
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <ChartPieIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Status Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { key: "sent", label: "Sent Today", color: "blue", icon: PaperAirplaneIcon },
            { key: "delivered", label: "Delivered Today", color: "green", icon: CheckCircleIcon },
            { key: "read", label: "Read Today", color: "yellow", icon: EyeIcon },
            { key: "failed", label: "Failed Today", color: "red", icon: XMarkIcon },
          ].map((metric) => {
            const value = safeToday[metric.key] || 0
            const percentage = getBoundedPercentage(value, attemptedToday)

            return (
              <div
                key={metric.key}
                className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 text-center"
              >
                <div className="mb-4">
                  <div
                    className={`w-16 h-16 bg-gradient-to-br from-${metric.color}-500 to-${metric.color}-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg`}
                  >
                    <metric.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {value.toLocaleString()}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">{metric.label}</div>
                <div className="text-gray-500 dark:text-gray-400 text-sm">
                  {percentage}% of attempted
                </div>
              </div>
            )
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* System Message Analytics */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2 text-green-600" />
                  System Message Analytics (Last 7 Days)
                </h3>
              </div>
            </div>
            <div className="p-6 h-80">
              <Line data={messageChartData} options={chartOptions} />
            </div>
          </div>

          {/* Status Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <ChartPieIcon className="w-5 h-5 mr-2 text-green-600" />
                Status Distribution
              </h3>
            </div>
            <div className="p-6 h-80">
              <Doughnut data={statusChartData} options={doughnutOptions} />
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <UsersIcon className="w-5 h-5 mr-2 text-green-600" />
                Managed System Users Overview
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Real-time user balances and today's message statistics.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search user..."
                  className="pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Roles</option>
                <option value="reseller">Reseller</option>
                <option value="company">Company</option>
                <option value="user">User</option>
                <option value="agent">Agent</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 text-xs font-semibold uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Operator</th>
                  <th className="py-3 px-4">Current Balance</th>
                  <th className="py-3 px-4 text-center">Today Sent</th>
                  <th className="py-3 px-4 text-center">Today Delivered</th>
                  <th className="py-3 px-4 text-center">Today Read</th>
                  <th className="py-3 px-4 text-center">Today Failed</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="py-8 text-center text-gray-500 dark:text-gray-400">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u, idx) => {
                    const stats = userStatsMap[u._id] || { sent: 0, delivered: 0, read: 0, failed: 0 }
                    const userBal = typeof u.balance === "number" ? u.balance : Number(u.balance) || 0

                    return (
                      <tr key={u._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                        <td className="py-3.5 px-4 font-medium text-gray-400">{idx + 1}</td>
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-gray-900 dark:text-white">{u.username || "User"}</div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">{u.email}</div>
                          {u.number && <div className="text-gray-400 text-xs">{u.number}</div>}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 capitalize">
                            {u.role || "user"}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          {(() => {
                            const op = String(u.operator || "cloud").toLowerCase()
                            let code = "C"
                            let badgeStyle = "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-200"
                            if (op.includes("karix")) {
                              code = "K"
                              badgeStyle = "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/50 dark:text-purple-200"
                            } else if (op.includes("pin")) {
                              code = "P"
                              badgeStyle = "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-200"
                            } else if (op.includes("fast")) {
                              code = "F"
                              badgeStyle = "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/50 dark:text-amber-200"
                            }
                            return (
                              <span
                                title={`Operator: ${u.operator || "cloud"}`}
                                className={`w-7 h-7 rounded-full inline-flex items-center justify-center font-extrabold text-xs border shadow-sm ${badgeStyle}`}
                              >
                                {code}
                              </span>
                            )
                          })()}
                        </td>
                        <td className="py-3.5 px-4 font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(userBal)}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-blue-600 dark:text-blue-400">
                          {stats.sent}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-green-600 dark:text-green-400">
                          {stats.delivered}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-yellow-600 dark:text-yellow-400">
                          {stats.read}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-red-600 dark:text-red-400">
                          {stats.failed}
                        </td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              (u.userStatus || "active") === "active"
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            }`}
                          >
                            {u.userStatus || "active"}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <Link to="/admin-recharge">
                            <button className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-600 to-yellow-600 hover:from-green-700 hover:to-yellow-700 text-white rounded-lg text-xs font-semibold transition-all shadow-sm">
                              <CreditCardIcon className="w-3.5 h-3.5 mr-1" />
                              Recharge
                            </button>
                          </Link>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
